import { useEffect, useState } from 'react';
import { positionValues } from 'react-custom-scrollbars-2';
import { useDispatch, useSelector } from 'react-redux';

import { IMarketFiltersForm } from 'models/market/i-market-filters-form';
import { IMarketFilters } from 'models/market/i-market-filters.interface';
import { IState } from 'models/reducers/i-state.interface';
import { ISelectOption } from 'models/select/i-select-option.interface';
import { IPriceRangeFieldValue } from 'routes/market/priceRangeField/models/i-price-range-field-value.interface';

import { MarketFiltersFields } from 'enums/market/market-filters-fields.enum';
import { MarketStatus } from 'enums/market/market-status.enum';

import { MARKET_CATEGORY_DEFAULT_OPTION, MARKET_FILTERS } from 'constants/market.constant';

import { appendMarketOffersAction, fetchMarketOffersAction, resetMarketDataAction } from 'actions/market.action';

import { useMarketEvents } from '../events/useMarketEvents';
import { useLoadingTime } from '../useLoadingTime';
import { useNftsPageSize } from './useNftsPageSize';

export const useNftsFilters = () => {
  const defaultCategory = MARKET_CATEGORY_DEFAULT_OPTION;
  const minScrollThreshold = 300;
  const { statuses, ordering } = MARKET_FILTERS;

  const {
    currentPage,
    firstPage,
    isLastPage,
    loading,
    marketItems: offers,
  } = useSelector((state: IState) => state.market);
  const dispatch = useDispatch();

  const [requestPending, setRequestPending] = useState(true);
  const [newPage, setNewPage] = useState(firstPage);
  const [filters, setFilters] = useState<IMarketFiltersForm>({
    [MarketFiltersFields.ordering]: ordering[0],
    [MarketFiltersFields.category]: { ...defaultCategory },
    [MarketFiltersFields.statuses]: [],
    [MarketFiltersFields.priceRange]: null,
    [MarketFiltersFields.revenuePeriod]: null,
    [MarketFiltersFields.search]: '',
  });

  const { sendMarketGetFirstPageEvent } = useMarketEvents();
  const { elementRef: listElementRef, pageSize } = useNftsPageSize();
  const { loadingTime, handleStartLoadingTime, handleStopLoadingTime } = useLoadingTime();

  useEffect(() => {
    loading ? handleStartLoadingTime() : handleStopLoadingTime();
  }, [loading]);

  useEffect(() => {
    if (offers && loadingTime && loadingTime > 0 && firstPage === currentPage) {
      sendMarketGetFirstPageEvent({ time: loadingTime });
    }
  }, [offers, loadingTime, firstPage, currentPage]);

  useEffect(() => {
    if (!pageSize) {
      return;
    }

    dispatch(resetMarketDataAction());
    setRequestPending(true);
    fetchOffers(pageSize);
  }, [filters, pageSize]);

  useEffect(() => {
    setNewPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (newPage === currentPage || !pageSize) {
      return;
    }

    setRequestPending(true);
    dispatch(appendMarketOffersAction(getFilters(newPage, pageSize)));
  }, [newPage, pageSize]);

  useEffect(() => {
    setRequestPending(false);
  }, [offers]);

  const getFilters = (page = newPage, pageSize: number): IMarketFilters => {
    const priceRange = filters[MarketFiltersFields.priceRange];
    const revenuePeriod = filters[MarketFiltersFields.revenuePeriod];
    const hasPriceLimits = priceRange && priceRange.currency && (priceRange.minValue || priceRange.maxValue);
    const hasPeriodLimits = revenuePeriod && revenuePeriod.period && (revenuePeriod.minValue || revenuePeriod.maxValue);

    return {
      page,
      limit: pageSize,
      search: filters[MarketFiltersFields.search],
      statuses: filters[MarketFiltersFields.statuses],
      category: filters[MarketFiltersFields.category]?.value!,
      priceCurrency: hasPriceLimits ? +priceRange!.currency!.value : undefined,
      pricesMin: hasPriceLimits ? priceRange?.minValue : undefined,
      pricesMax: hasPriceLimits ? priceRange?.maxValue : undefined,
      revenuePeriod: hasPeriodLimits ? revenuePeriod.period!.value : undefined,
      revenueMin: hasPeriodLimits ? revenuePeriod?.minValue : undefined,
      revenueMax: hasPeriodLimits ? revenuePeriod?.maxValue : undefined,
      ordering: filters[MarketFiltersFields.ordering]?.value,
    };
  };

  const handleStatusChange = (status: MarketStatus, checked: boolean) => {
    let updatedCheckedStatuses: MarketStatus[];

    if (checked) {
      updatedCheckedStatuses = [...filters[MarketFiltersFields.statuses], status];
    } else {
      updatedCheckedStatuses = [
        ...filters[MarketFiltersFields.statuses].filter((checkedStatus) => checkedStatus !== status),
      ];
    }

    updateFilters(MarketFiltersFields.statuses, [...updatedCheckedStatuses]);
  };

  const handlePriceRangeChange = (value: IPriceRangeFieldValue) => {
    updateFilters(MarketFiltersFields.priceRange, value);
  };

  const handleCategoryChange = (value: ISelectOption) => {
    updateFilters(MarketFiltersFields.category, value);
  };

  const updateFilters = (key: MarketFiltersFields, value: IMarketFiltersForm[keyof IMarketFiltersForm]) => {
    setFilters({ ...filters, [key]: value });
  };

  const fetchOffers = (pageSize: number) => {
    dispatch(fetchMarketOffersAction(getFilters(firstPage, pageSize)));
  };

  const lazyLoadItems = (scrollEvent: positionValues) => {
    const scrollOffset = scrollEvent.scrollHeight - scrollEvent.scrollTop - scrollEvent.clientHeight;

    if (offers?.length && !requestPending && !isLastPage && scrollOffset < minScrollThreshold) {
      setNewPage(currentPage + 1);
    }
  };

  return {
    filters,
    listElementRef,
    loading,
    ordering,
    requestPending,
    statuses,
    handleCategoryChange,
    handlePriceRangeChange,
    handleStatusChange,
    lazyLoadItems,
    updateFilters,
  };
};
